import { PERMISSION_KEYS, usePermissionCheck } from '@/features/conversation';
import { Block, CreateNewFolder, KeyboardVoice } from '@mui/icons-material';
import { Alert, Box, Button, Card, IconButton, Stack, TextField, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessageInput } from '../../hooks/use-message-input';
import { Attachment } from '../../models/attachment';
import type { AudioRecordingData } from '../../models/audio-recorder.types';
import { AudioRecorder } from '../audio-recorder/audio-recorder';
import { AttachmentTypeMenu } from './attachment-type-menu';
import MessageInputEmojiPicker from './message-input.emoji-picker';
import MessageInputFile from './message-input.file';
import { MessageInputReply } from './message-input.reply';

/**
 * MessageInput Component - Open-Closed Principle & Liskov Substitution
 * Uses composition for UI elements and delegates logic to custom hook
 * Interface Segregation: Only exposes necessary props
 */
interface MessageInputProps {
  convId: Nullable<string>;
  placeholder?: string;
  disabled?: boolean;
}

export const MessageInput = ({ convId, placeholder, disabled = false }: MessageInputProps) => {
  const { t } = useTranslation();
  // Audio recording state
  const [isAudioMode, setIsAudioMode] = useState(false);

  // Permission check - determines if user can send messages
  const { hasPermission } = usePermissionCheck(convId);
  const canSendMessage = hasPermission(PERMISSION_KEYS.MESSAGE_SEND);
  const isDisabled = disabled || !canSendMessage;

  // Use placeholder from props or default translation
  const inputPlaceholder = placeholder || t('layout.chatInput.placeholder');

  // Use custom hook for all logic - Single Responsibility delegated
  const {
    value,
    setValue,
    attachments,
    setAttachments,
    attachmentMode,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    openWithMode,
    handleSendMsg,
    onKeyDown,
    handlePaste,
    isSendDisabled
  } = useMessageInput(convId);

  // Toggle audio recording mode
  const handleToggleAudioMode = useCallback(() => {
    setIsAudioMode((prev) => !prev);
  }, []);

  // Handle send audio
  const handleSendAudio = useCallback(
    async (data: AudioRecordingData) => {
      // Convert blob to File
      const file = new File([data.blob], `voice-message-${Date.now()}.${data.format.fileExtension}`, {
        type: data.mimeType
      });
      const audioAttachment = new Attachment(file, data.waveformData);

      // Send immediately
      await handleSendMsg(audioAttachment);

      // Reset audio state
      setIsAudioMode(false);
    },
    [setAttachments, handleSendMsg]
  );

  // Handle cancel audio mode
  const handleCancelAudioMode = useCallback(() => {
    setIsAudioMode(false);
  }, []);

  return (
    <Box className='mex:flex mex:min-h-[120px] mex:items-end mex:justify-center mex:gap-3 mex:bg-background-default mex:p-4'>
      <AnimatePresence mode='wait' initial={false}>
        {isAudioMode ? (
          <motion.div
            key='audio-recorder'
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1]
            }}
            className='mex:w-[50%] mex:max-w-full'
          >
            <AudioRecorder onSend={handleSendAudio} onCancel={handleCancelAudioMode} />
          </motion.div>
        ) : (
          <motion.div
            key='message-input'
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1]
            }}
            className='mex:w-full'
          >
            {/* Permission warning */}

            <Card
              id='message-input-card'
              {...getRootProps({ className: 'mex:dropzone' })}
              className='mex:w-full mex:border mex:border-primary-lighter mex:bg-background-default mex:p-2 mex:shadow-2'
              variant='outlined'
            >
              {!canSendMessage && (
                <Alert
                  severity='warning'
                  className='mex:absolute mex:top-0 mex:right-0 mex:bottom-0 mex:left-0 mex:z-40 mex:items-center mex:justify-center'
                >
                  {t('layout.chatInput.noSendPermission')}
                </Alert>
              )}
              {/* Files DropZone */}
              <input {...getInputProps()} />
              {isDragActive && (
                <Box
                  component='div'
                  className='mex:absolute mex:top-0 mex:right-0 mex:bottom-0 mex:left-0 mex:z-30 mex:bg-gray-50/80'
                >
                  <Stack direction='row' justifyContent='center' alignItems='center' gap={1} className='mex:h-full'>
                    {isDragAccept && (
                      <>
                        <CreateNewFolder className='mex:text-primary' />
                        <Typography className='mex:text-primary'>{t('messages.upload.dropFilesHere')}</Typography>
                      </>
                    )}
                    {isDragReject && (
                      <>
                        <Block className='mex:text-red-500' />
                        <Typography className='mex:text-red-500'>{t('messages.upload.fileNotAccepted')}</Typography>
                      </>
                    )}
                  </Stack>
                </Box>
              )}

              <MessageInputFile data={attachments} onRemove={setAttachments} />
              <MessageInputReply convId={convId} />

              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder={inputPlaceholder}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={onKeyDown}
                onPaste={handlePaste}
                variant='standard'
                slotProps={{
                  input: {
                    disableUnderline: true
                  }
                }}
                size='medium'
                className='mex:!mb-2 mex:flex-1'
                disabled={isDisabled}
              />

              <Stack direction='row' justifyContent='space-between' alignItems='center' className='mex:!w-full'>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <MessageInputEmojiPicker
                    onSelect={(emoji: string) => setValue((prev) => prev + emoji)}
                    disabled={isDisabled}
                  />
                  <IconButton
                    size='small'
                    onClick={handleToggleAudioMode}
                    disabled={isDisabled}
                    aria-label={t('layout.chatInput.recordAudio')}
                  >
                    <KeyboardVoice />
                  </IconButton>
                  <AttachmentTypeMenu
                    onSelectMedia={() => openWithMode('media')}
                    onSelectFiles={() => openWithMode('files')}
                    disabled={isDisabled}
                    activeMode={attachmentMode}
                  />
                </Stack>
                <Button
                  variant='contained'
                  size='small'
                  color='primary'
                  disabled={isSendDisabled(isDisabled)}
                  onClick={() => handleSendMsg()}
                  sx={{ minWidth: 60 }}
                >
                  {t('layout.chatInput.send')}
                </Button>
              </Stack>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};
